import { useState, useCallback } from 'react';
import LogoImg from '../images/isa2.png';
import LogoImgSad from '../images/isa_sad2.png';

/**
 * Manages loan-modal state and all loan-related API operations.
 */
const useLoanModal = ({ apiCHAT, tokenChat, setMessages }) => {
    const [showLoanModal, setShowLoanModal] = useState(false);
    const [loanData, setLoanData] = useState(null);
    const [loadingLoanData, setLoadingLoanData] = useState(false);

    // ─── Fetch ────────────────────────────────────────────────────────────────

    const fetchLoanData = useCallback(async () => {
        if (!tokenChat) return null;

        setLoadingLoanData(true);
        try {
            const response = await apiCHAT.get('/proposta_emprestimos/dados_cliente_pessoa_id', {
                headers: { Authorization: `Bearer ${tokenChat}` },
            });
            setLoanData(response.data);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar dados do empréstimo:', error);
            const errorMessage =
                error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                'Erro ao carregar dados do empréstimo. Tente novamente.';
            setMessages((prev) => [
                ...prev,
                { remetente: 'ISA', texto: errorMessage, avatar: LogoImgSad, isOption: false },
            ]);
            return null;
        } finally {
            setLoadingLoanData(false);
        }
    }, [apiCHAT, tokenChat, setMessages]);

    // ─── Save ─────────────────────────────────────────────────────────────────

    const saveLoanData = useCallback(
        async (formData) => {
            if (!tokenChat) return false;

            try {
                await apiCHAT.post('/zipdin/salvar-dados-complementares', formData, {
                    headers: { Authorization: `Bearer ${tokenChat}` },
                });
                return true;
            } catch (error) {
                console.error('Erro ao salvar dados do empréstimo:', error);
                const errorMessage =
                    error.response?.data?.detail ||
                    error.response?.data?.message ||
                    error.message ||
                    'Erro ao salvar dados bancários. Tente novamente.';
                setMessages((prev) => [
                    ...prev,
                    { remetente: 'ISA', texto: errorMessage, avatar: LogoImgSad, isOption: false },
                ]);
                return false;
            }
        },
        [apiCHAT, tokenChat, setMessages],
    );

    // ─── Open ─────────────────────────────────────────────────────────────────

    const openLoanModal = useCallback(async () => {
        const data = await fetchLoanData();
        if (data) {
            setShowLoanModal(true);
        } else {
            setMessages((prev) => [
                ...prev,
                { remetente: 'ISA', texto: 'Erro ao carregar dados. Tente novamente.', avatar: LogoImgSad, isOption: false },
            ]);
        }
    }, [fetchLoanData, setMessages]);

    // ─── Form submit ──────────────────────────────────────────────────────────

    const handleLoanFormSubmit = useCallback(
        async (formData, { setOptions, setShowOptions, setShowInputBar, setNewTipoChat }) => {
            const success = await saveLoanData(formData);
            setShowLoanModal(false);

            if (success) {
                setNewTipoChat('emprestimo_finalizar');
                setMessages((prev) => [
                    ...prev,
                    {
                        remetente: 'ISA',
                        texto: 'Dados salvos com sucesso! Agora você pode finalizar seu empréstimo.',
                        avatar: LogoImg,
                        isOption: false,
                    },
                ]);

                setOptions([
                    { key: 'credito_finalizar', label: 'Aderir' },
                    { key: 'credito_cancelar', label: 'Cancelar proposta' },
                ]);

                setMessages((prev) => [
                    ...prev,
                    {
                        remetente: 'ISA',
                        texto: 'Gostaria de aderir ao empréstimo consignado? Se sim, clique em aderir ou cancele a proposta',
                        avatar: LogoImg,
                        isOption: false,
                    },
                ]);

                setShowOptions(true);
                setShowInputBar(false);
            } else {
                setMessages((prev) => [
                    ...prev,
                    { remetente: 'ISA', texto: 'Erro ao salvar dados. Tente novamente.', avatar: LogoImgSad, isOption: false },
                ]);
            }
        },
        [saveLoanData, setMessages],
    );

    return {
        showLoanModal,
        setShowLoanModal,
        loanData,
        loadingLoanData,
        fetchLoanData,
        saveLoanData,
        openLoanModal,
        handleLoanFormSubmit,
    };
};

export default useLoanModal;
